import * as ts from "typescript"

import { resolveModuleSpecifierFromFile } from "../../../utils/module-resolution.js"
import { Parser } from "./parser.js"
import { ComponentDoc, ParserOptions } from "./types.js"
import { filterDuplicates } from "./utils/filter-duplicates.js"

type ComponentExportType = "explicit-export" | "inferred-export" | "star-export"

type NamedExportBinding = {
  exportedName: string
  importedName: string
}

function applyExportType(componentDocs: ComponentDoc[], exportType: ComponentExportType): ComponentDoc[] {
  return componentDocs.map(componentDoc => ({ ...componentDoc, exportType }))
}

export class ModuleParser {
  private checker: ts.TypeChecker
  private entryFilePath: string
  private parser: Parser
  private program: ts.Program

  constructor(program: ts.Program, entryFilePath: string, parserOptions: ParserOptions = {}) {
    this.program = program
    this.parser = new Parser(program, parserOptions)
    this.entryFilePath = entryFilePath
    this.checker = program.getTypeChecker()
  }

  public parseModule(): ComponentDoc[] {
    const sourceFile = this.program.getSourceFile(this.entryFilePath)

    if (!sourceFile) {
      throw new Error(`Could not find source file for path ${this.entryFilePath}`)
    }

    const componentsInfo = this.parseFile(sourceFile)
    return filterDuplicates(componentsInfo)
  }

  private parseResolvedNamedReExport(
    scannedSource: ts.SourceFile,
    modulePath: string,
    unresolvedExports: NamedExportBinding[],
    exportType: ComponentExportType,
  ): ComponentDoc[] {
    const resolvedPath = resolveModuleSpecifierFromFile(scannedSource.fileName, modulePath)
    if (!resolvedPath) {
      return []
    }

    const newScannedSource = this.program.getSourceFile(resolvedPath)
    if (!newScannedSource) {
      return []
    }

    const moduleSymbol = this.checker.getSymbolAtLocation(newScannedSource)
    if (!moduleSymbol) {
      return []
    }

    const exportsByName = new Map(this.checker.getExportsOfModule(moduleSymbol).map(symbol => [symbol.getName(), symbol]))
    const componentDocs: ComponentDoc[] = []

    for (const binding of unresolvedExports) {
      const symbol = exportsByName.get(binding.importedName)
      if (!symbol) {
        continue
      }

      const parsedDocs = applyExportType(this.parser.parseExportSymbol(symbol, newScannedSource), exportType).map(
        componentDoc =>
          binding.exportedName === componentDoc.exportName
            ? componentDoc
            : { ...componentDoc, exportName: binding.exportedName },
      )
      componentDocs.push(...parsedDocs)
    }

    return componentDocs
  }

  private parseFile(scannedSource: ts.SourceFile, exportType: ComponentExportType = "explicit-export"): ComponentDoc[] {
    const componentsInfo: ComponentDoc[] = []

    const moduleSymbol = this.checker.getSymbolAtLocation(scannedSource)
    if (!moduleSymbol) {
      throw new Error(`Could not find module symbol for source file: ${scannedSource.fileName}`)
    }

    // iterate through file symbols. We cannot use just getModuleExports. It don't show all exports. This is more reliable.
    ts.forEachChild(scannedSource, node => {
      // Handle named exports (export { X })
      if (ts.isExportDeclaration(node) && node.exportClause && ts.isNamedExports(node.exportClause)) {
        const unresolvedReExports: NamedExportBinding[] = []

        for (const exportSpecifier of node.exportClause.elements) {
          if (node.isTypeOnly || exportSpecifier.isTypeOnly) {
            continue
          }

          const importedName = exportSpecifier.propertyName?.text ?? exportSpecifier.name.text
          const exportedName = exportSpecifier.name.text
          const symbol = this.checker.getSymbolAtLocation(exportSpecifier.name)
          let parsedDocs: ComponentDoc[] = []
          if (symbol) {
            parsedDocs = this.parser.parseExportSymbol(symbol, scannedSource)
            if (parsedDocs.length > 0) {
              componentsInfo.push(...applyExportType(parsedDocs, exportType))
            }
          }

          if (parsedDocs.length === 0 && node.moduleSpecifier && ts.isStringLiteral(node.moduleSpecifier)) {
            unresolvedReExports.push({ exportedName, importedName })
          }
        }

        if (node.moduleSpecifier && ts.isStringLiteral(node.moduleSpecifier) && unresolvedReExports.length > 0) {
          componentsInfo.push(
            ...this.parseResolvedNamedReExport(
              scannedSource,
              node.moduleSpecifier.text,
              unresolvedReExports,
              exportType,
            ),
          )
        }

        return
      }

      // Handle direct exports of functions and classes (export class X, export const X, etc)
      if ((ts.isClassDeclaration(node) || ts.isFunctionDeclaration(node)) && hasExportModifier(node)) {
        const symbol = node.name ? this.checker.getSymbolAtLocation(node.name) : undefined
        if (symbol) {
          componentsInfo.push(...applyExportType(this.parser.parseExportSymbol(symbol, scannedSource), exportType))
        }

        return
      }

      // handle direct export of variables, e.g. export const X
      if (ts.isVariableStatement(node) && hasExportModifier(node)) {
        for (const declaration of node.declarationList.declarations) {
          if (ts.isIdentifier(declaration.name)) {
            const symbol = this.checker.getSymbolAtLocation(declaration.name)
            if (symbol) {
              componentsInfo.push(
                ...applyExportType(this.parser.parseExportSymbol(symbol, scannedSource), exportType),
              )
            }
          }
        }

        return
      }

      // Handle default exports (export default X)
      if (ts.isExportAssignment(node)) {
        const symbol = this.checker.getSymbolAtLocation(node.expression)
        if (symbol) {
          componentsInfo.push(...applyExportType(this.parser.parseExportSymbol(symbol, scannedSource), exportType))
        }

        return
      }

      // Handle re-exports (export * from 'module')
      if (ts.isExportDeclaration(node) && node.moduleSpecifier && ts.isStringLiteral(node.moduleSpecifier)) {
        const modulePath = node.moduleSpecifier.text

        // star export = export * from 'module'
        const isStarExport = !node.exportClause

        if (!isStarExport) {
          return
        }

        const resolvedPath = resolveModuleSpecifierFromFile(scannedSource.fileName, modulePath)
        if (!resolvedPath) {
          // ignore star export if module path does not exist
          return
        }

        const newScannedSource = this.program.getSourceFile(resolvedPath)
        if (newScannedSource) {
          const newComponents = this.parseFile(newScannedSource, "star-export")
          componentsInfo.push(...newComponents)
        }
      }
    })

    return componentsInfo
  }
}

// Helper function to check if node has export modifier
function hasExportModifier(node: ts.Node): boolean {
  return (ts.canHaveModifiers(node) && node.modifiers?.some(mod => mod.kind === ts.SyntaxKind.ExportKeyword)) || false
}
